import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getRun,
  lockPlacement,
  movePlacement,
} from "../../services/timetableGenerationService";
import { getTimetableSettings } from "../../services/schedulingService";
// One definition of "in flight", shared with the status badge, so the poll loop
// and the UI can never disagree about when a run has stopped moving.
import { isRunInFlight } from "./RunStatusPanel";

// ---------------------------------------------------------------------------
// The draft board's state machine.
//
// One hook owns everything the aSc-style board needs: the run, its cards, the
// click-to-place selection, and the two mutations (move / lock). It is the only
// place that talks to the generation API, so the page and the grid stay pure
// renderers.
//
// Two deliberate choices worth knowing before editing:
//
// * Mutations are OPTIMISTIC WITH ROLLBACK. `move` and `lock` both return the
//   updated card, so the success path replaces the optimistic row with the
//   server's own copy — the board can never drift from the backend. On failure
//   the pre-move snapshot is restored, so a rejected drag never leaves a lie on
//   screen.
// * Conflict feedback is computed CLIENT-SIDE from the same rule the server
//   applies (`organization.services.generation._slot_conflicts`): a slot clashes
//   when the card's class, or any of its teachers, already owns an overlapping
//   period that weekday. This only paints the targets red/green — the server
//   stays the authority and a 400 still wins.
// ---------------------------------------------------------------------------

const POLL_MS = 2500;

// Grid fallbacks, used only when the caller has no TimetableSettings to pass.
// Mon–Fri is always shown so a day the solver happened to leave empty does not
// silently vanish from the week.
const DEFAULT_WEEKDAYS = [1, 2, 3, 4, 5];
const DEFAULT_PERIOD_COUNT = 8;

export const slotKey = (weekday, period) => `${weekday}:${period}`;

const cardDuration = (card) => Math.max(1, Number(card?.duration) || 1);

/** The periods a card physically occupies (duration > 1 spans consecutive ones). */
export const coveredPeriods = (card) => {
  const start = card.period;
  const out = [];
  for (let i = 0; i < cardDuration(card); i += 1) out.push(start + i);
  return out;
};

/**
 * Board state for one generation run.
 *
 * @param runUuid the run to render.
 * @param opts.settings an already-loaded TimetableSettings. Omit it and the
 *                 hook fetches the caller-school singleton itself; if that is
 *                 unavailable the week/bell axes fall back to being derived
 *                 from the cards, so the board always renders.
 */
export default function useBoard(runUuid, { settings: settingsProp } = {}) {
  const [run, setRun] = useState(null);
  const [fetchedSettings, setFetchedSettings] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState(null);
  const [busyUuid, setBusyUuid] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Refs mirror state for callbacks that must not re-create on every edit.
  const placementsRef = useRef(placements);
  placementsRef.current = placements;
  const busyRef = useRef(busyUuid);
  busyRef.current = busyUuid;

  const status = run?.status;

  // ----------------------------------------------------------------- loading
  const load = useCallback(
    (signal, silent = false) => {
      if (!runUuid) return Promise.resolve();
      if (!silent) setLoading(true);
      return getRun({ uuid: runUuid, signal })
        .then((data) => {
          setRun(data);
          setLoadError(false);
          // A poll must never stomp a move that is still in flight.
          if (silent && busyRef.current) return;
          setPlacements(Array.isArray(data?.placements) ? data.placements : []);
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED") return;
          if (!silent) setLoadError(true);
        })
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [runUuid],
  );

  useEffect(() => {
    const controller = new AbortController();
    setSelectedUuid(null);
    setActionError(null);
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Poll only while the solver is actually working; stop dead on any terminal
  // status so a finished board does not hammer the API.
  useEffect(() => {
    if (!runUuid || !isRunInFlight(status)) return undefined;
    const controller = new AbortController();
    const id = setInterval(() => load(controller.signal, true), POLL_MS);
    return () => {
      clearInterval(id);
      controller.abort();
    };
  }, [runUuid, status, load]);

  const reload = useCallback(() => load(undefined, false), [load]);

  // The bell schedule is reference data for the caller's own school and never
  // changes mid-session, so it is fetched once and is not part of the poll.
  // A failure here is non-fatal: the axes fall back to the cards themselves.
  useEffect(() => {
    if (settingsProp) return undefined;
    const controller = new AbortController();
    getTimetableSettings({ signal: controller.signal })
      .then((data) => setFetchedSettings(data || null))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setFetchedSettings(null);
      });
    return () => controller.abort();
  }, [settingsProp]);

  const settings = settingsProp || fetchedSettings;

  // ------------------------------------------------------------- grid axes
  // Both axes are the UNION of the configured week and what the cards actually
  // use. The grid only renders cards whose slot exists on an axis, so a card
  // sitting outside the configured week — a stale run, or a head-office user
  // whose own school's bell schedule differs from the one being viewed — would
  // otherwise disappear from the board AND become unmovable. Union can only
  // add an empty column or row; it can never hide a lesson.
  const weekdays = useMemo(() => {
    const fromSettings = settings?.teaching_weekdays;
    const seen = new Set(
      Array.isArray(fromSettings) && fromSettings.length > 0 ? fromSettings : DEFAULT_WEEKDAYS,
    );
    placements.forEach((card) => {
      if (card.weekday != null) seen.add(card.weekday);
    });
    return [...seen].sort((a, b) => a - b);
  }, [settings, placements]);

  const periods = useMemo(() => {
    const fromSettings = settings?.periods;
    const configured =
      Array.isArray(fromSettings) && fromSettings.length > 0
        ? fromSettings.map((p) => ({ period: p.period, start: p.start, end: p.end }))
        : Array.from({ length: DEFAULT_PERIOD_COUNT }, (_, i) => ({ period: i + 1 }));

    const byPeriod = new Map(configured.map((entry) => [entry.period, entry]));
    let max = configured.reduce((acc, entry) => Math.max(acc, entry.period), 0);
    placements.forEach((card) => {
      if (card.period != null) {
        max = Math.max(max, card.period + cardDuration(card) - 1);
      }
    });
    for (let period = 1; period <= max; period += 1) {
      if (!byPeriod.has(period)) byPeriod.set(period, { period });
    }
    return [...byPeriod.values()].sort((a, b) => a.period - b.period);
  }, [settings, placements]);

  const lastPeriod = periods.length > 0 ? periods[periods.length - 1].period : 0;

  // ------------------------------------------------------------- occupancy
  // `resource|weekday|period -> cards`, expanded across each card's duration.
  // Built once per placements change and reused for every candidate slot, so
  // painting a full week of targets is a handful of Map lookups, not a scan.
  const occupancy = useMemo(() => {
    const index = new Map();
    const push = (key, card) => {
      const bucket = index.get(key);
      if (bucket) bucket.push(card);
      else index.set(key, [card]);
    };
    placements.forEach((card) => {
      if (card.weekday == null || card.period == null) return;
      coveredPeriods(card).forEach((period) => {
        push(`g|${card.group}|${card.weekday}|${period}`, card);
        (card.teachers || []).forEach((teacher) => {
          push(`t|${teacher.uuid}|${card.weekday}|${period}`, card);
        });
      });
    });
    return index;
  }, [placements]);

  /**
   * Why the given card cannot go to the given slot — mirrors the server rule.
   * Returns [] for a legal slot.
   */
  const conflictsAt = useCallback(
    (card, weekday, period) => {
      if (!card) return [];
      const duration = cardDuration(card);
      if (period + duration - 1 > lastPeriod) {
        return [{ kind: "overflow", label: String(duration) }];
      }
      const found = new Map();
      for (let i = 0; i < duration; i += 1) {
        const at = period + i;
        (occupancy.get(`g|${card.group}|${weekday}|${at}`) || []).forEach((other) => {
          if (other.uuid !== card.uuid) {
            found.set(`class:${other.group}`, { kind: "class", label: other.group });
          }
        });
        (card.teachers || []).forEach((teacher) => {
          (occupancy.get(`t|${teacher.uuid}|${weekday}|${at}`) || []).forEach((other) => {
            if (other.uuid !== card.uuid) {
              found.set(`teacher:${teacher.uuid}`, { kind: "teacher", label: teacher.name });
            }
          });
        });
      }
      return [...found.values()];
    },
    [occupancy, lastPeriod],
  );

  const selected = useMemo(
    () => placements.find((card) => card.uuid === selectedUuid) || null,
    [placements, selectedUuid],
  );

  /** `weekday:period -> conflicts[]` for the current selection (null when idle). */
  const targetMap = useMemo(() => {
    if (!selected) return null;
    const map = new Map();
    weekdays.forEach((weekday) => {
      periods.forEach(({ period }) => {
        map.set(slotKey(weekday, period), conflictsAt(selected, weekday, period));
      });
    });
    return map;
  }, [selected, weekdays, periods, conflictsAt]);

  // ------------------------------------------------------------- selection
  const select = useCallback((uuid) => {
    setActionError(null);
    setSelectedUuid((current) => (current === uuid ? null : uuid));
  }, []);

  const clearSelection = useCallback(() => setSelectedUuid(null), []);
  const dismissError = useCallback(() => setActionError(null), []);

  // Escape cancels a pending placement; Ctrl/Cmd+Z undoes the last move.
  // Registered unconditionally — it used to be gated on there being a selection,
  // which is exactly when undo is NOT wanted. `undoRef` keeps the handler stable
  // so this listener is attached once rather than re-bound on every move.
  const undoRef = useRef(null);
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {
        setSelectedUuid(null);
        return;
      }
      const target = event.target;
      const tag = target?.tagName;
      // Never steal undo from a text field the user is typing in.
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        undoRef.current?.();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // ------------------------------------------------------------- mutations
  // Undo stack of pre-move positions. A ref (not state) because every mutation
  // reads it without needing to re-render on push; `undoDepth` mirrors its size
  // purely so the toolbar button can disable itself.
  const historyRef = useRef([]);
  const [undoDepth, setUndoDepth] = useState(0);

  const replaceCard = useCallback((uuid, next) => {
    setPlacements((current) =>
      current.map((card) => (card.uuid === uuid ? next : card)),
    );
  }, []);

  const moveTo = useCallback(
    // `record` is false only when the move IS an undo — otherwise undoing would
    // push its own inverse and the stack would flip between two states forever.
    async (uuid, weekday, period, { record = true } = {}) => {
      const snapshot = placementsRef.current.find((card) => card.uuid === uuid);
      if (!snapshot || busyRef.current) return;
      // No-op move: treat the click as "put it back", i.e. just deselect.
      if (snapshot.weekday === weekday && snapshot.period === period) {
        setSelectedUuid(null);
        return;
      }
      const toTray = weekday == null || period == null;
      setBusyUuid(uuid);
      busyRef.current = uuid;
      setActionError(null);
      replaceCard(uuid, {
        ...snapshot,
        weekday: toTray ? null : weekday,
        period: toTray ? null : period,
        is_placed: !toTray,
        unplaced_reason: toTray ? snapshot.unplaced_reason : "",
      });
      try {
        const updated = await movePlacement({
          uuid: runUuid,
          placementUuid: uuid,
          body: { weekday: toTray ? null : weekday, period: toTray ? null : period },
        });
        replaceCard(uuid, updated && updated.uuid ? updated : { ...snapshot, weekday, period });
        // Record where it CAME FROM. Every click on this board is an immediate
        // write to the server, so without this a mis-drop on a 190-card week is
        // unrecoverable except by regenerating and losing every other edit.
        if (record) {
          historyRef.current.push({
            uuid,
            weekday: snapshot.weekday ?? null,
            period: snapshot.period ?? null,
          });
          setUndoDepth(historyRef.current.length);
        }
        setSelectedUuid(null);
      } catch (err) {
        if (err?.code === "ERR_CANCELED") return;
        replaceCard(uuid, snapshot); // rollback — never show a rejected move
        const data = err?.response?.data;
        setActionError({
          code: data?.code || null,
          detail: data?.detail || null,
          details: Array.isArray(data?.details) ? data.details : [],
        });
      } finally {
        busyRef.current = null;
        setBusyUuid(null);
      }
    },
    [runUuid, replaceCard],
  );

  const sendToTray = useCallback((uuid) => moveTo(uuid, null, null), [moveTo]);

  // Put the last moved card back where it was. Server-side, so it is subject to
  // the same slot checks as any other move — if a colleague has taken the slot
  // in the meantime the undo is refused with the usual clash banner rather than
  // silently overwriting them.
  const undo = useCallback(async () => {
    const last = historyRef.current.pop();
    setUndoDepth(historyRef.current.length);
    if (!last) return;
    await moveTo(last.uuid, last.weekday, last.period, { record: false });
  }, [moveTo]);

  const toggleLock = useCallback(
    async (uuid) => {
      const snapshot = placementsRef.current.find((card) => card.uuid === uuid);
      if (!snapshot || busyRef.current) return;
      const next = !snapshot.is_locked;
      setBusyUuid(uuid);
      busyRef.current = uuid;
      setActionError(null);
      replaceCard(uuid, { ...snapshot, is_locked: next });
      try {
        const updated = await lockPlacement({
          uuid: runUuid,
          placementUuid: uuid,
          is_locked: next,
        });
        if (updated && updated.uuid) replaceCard(uuid, updated);
      } catch (err) {
        if (err?.code === "ERR_CANCELED") return;
        replaceCard(uuid, snapshot);
        const data = err?.response?.data;
        setActionError({
          code: data?.code || null,
          detail: data?.detail || null,
          details: Array.isArray(data?.details) ? data.details : [],
        });
      } finally {
        busyRef.current = null;
        setBusyUuid(null);
      }
    },
    [runUuid, replaceCard],
  );

  // ------------------------------------------------------------ derived sets
  const unplaced = useMemo(
    () => placements.filter((card) => !card.is_placed || card.weekday == null),
    [placements],
  );

  undoRef.current = undo;

  const stats = useMemo(() => {
    let placed = 0;
    let locked = 0;
    placements.forEach((card) => {
      if (card.is_placed && card.weekday != null) placed += 1;
      if (card.is_locked) locked += 1;
    });
    return { total: placements.length, placed, locked, unplaced: placements.length - placed };
  }, [placements]);

  return {
    run,
    status,
    placements,
    unplaced,
    stats,
    loading,
    loadError,
    reload,
    weekdays,
    periods,
    selected,
    selectedUuid,
    select,
    clearSelection,
    targetMap,
    busyUuid,
    moveTo,
    sendToTray,
    toggleLock,
    undo,
    canUndo: undoDepth > 0,
    actionError,
    dismissError,
  };
}
